import type { RecipeMainIngredient } from '@apps/main/modules/recipe/ingredients';

import type {
  ConnectionType,
  AvailabilityOrder,
  Landing,
  RecipeMetaData,
  Pax,
} from '@libs/trip-planner-interfaces';
import type { MonthIndex } from '@libs/sembo-dates';
import type { CustomerOriginQueryParamValue } from '@libs/feature-toggle';

export enum TripPlannerPage {
  BaseURL = 'plan',
  BuildYourTrip = 'buildYourTrip',
  TripOverview = 'tripOverview',
  TripOverviewPrint = 'tripOverviewPrint',
  HotelDetailsInTripOverview = 'hotelDetailsInTripOverview',
  ActivityDetailsInTripOverview = 'activityDetailsInTripOverview',
  FlightList = 'flights',
  FerryList = 'ferry',
  HotelSelection = 'hotelSelection',
  ActivitySelection = 'activitySelection',
  HotelDetailsInHotelSelection = 'hotelDetailsInHotelSelection',
  HotelDetails = 'hotelDetails',
  ActivityDetailsInActivitySelection = 'activityDetailsInActivitySelection',
  ActivityDetails = 'activityDetails',
  CarTrip = 'carTrip',
}

// NOTE: This parameter is used when some errors further down the funner occured
// and user is redirected back to trip planner to try again.
export const enum ExternalErrorQueryParamValue {
  // NOTE: This variant is used by book team to redirect user from checkout, when booking failed.
  BookingFailed = 'booking-failed',
}

export const enum ConfigurationPanelQueryParamValue {
  Calendar = 'calendar',
  Multistop = 'multistop',
}

export interface TripPlannerQueryParams {
  initialIngredients: RecipeMainIngredient[];
  initialPax: Pax;
  initialSelectedDate?: Date;
  initialSelectedMonth?: MonthIndex;
  initialSuggestionSortType?: AvailabilityOrder;
  landing?: Landing;
  initialMetaRooms?: string[];
  sessionId?: string;
  initialRedirectionToken?: string;
  returnTransport?: ConnectionType;
  metaData?: RecipeMetaData;
  aiTripId?: string;
  publishedTripId?: string;
  pat?: string;
  externalError?: ExternalErrorQueryParamValue;
  customerOrigin?: CustomerOriginQueryParamValue;
  panel?: ConfigurationPanelQueryParamValue;
  searchForPackageRates?: boolean | null;
  upsellBanner?: string;
}

const simplePagePaths = new Map<string, TripPlannerPage>([
  ['travel/plan', TripPlannerPage.BaseURL],
  ['travel/plan/build-your-trip', TripPlannerPage.BuildYourTrip],
  ['travel/plan/overview', TripPlannerPage.TripOverview],
  ['travel/plan/overview-print', TripPlannerPage.TripOverviewPrint],
  ['travel/plan/hotel-details', TripPlannerPage.HotelDetails],
  ['travel/plan/flights', TripPlannerPage.FlightList],
  ['travel/plan/ferry', TripPlannerPage.FerryList],
  ['travel/plan/activity-details', TripPlannerPage.ActivityDetails],
  ['travel/plan/car-trip', TripPlannerPage.CarTrip],
]);

const parametrizedPagePaths = new Map<RegExp, TripPlannerPage>([
  [
    /^travel\/plan\/hotel-selection\/h?([0-9]+)$/,
    TripPlannerPage.HotelSelection,
  ],
  [
    /^travel\/plan\/hotel-selection\/h?([0-9]+)\/hotel-details\/.+$/,
    TripPlannerPage.HotelDetailsInHotelSelection,
  ],
  [
    /^travel\/plan\/overview\/h?([0-9]+)\/hotel-details\/.+$/,
    TripPlannerPage.HotelDetailsInTripOverview,
  ],
  [
    /^travel\/plan\/activity-selection\/h?([0-9]+)$/,
    TripPlannerPage.ActivitySelection,
  ],
  [
    /^travel\/plan\/activity-selection\/h?([0-9]+)\/activity-details\/.+$/,
    TripPlannerPage.ActivityDetailsInActivitySelection,
  ],
  [
    /^travel\/plan\/overview\/h?([0-9]+)\/activity-details\/.+$/,
    TripPlannerPage.ActivityDetailsInTripOverview,
  ],
]);

export function mapPathToTripPlannerPage(
  path: string
): TripPlannerPage | undefined {
  // NOTE: remove trailing and leading slashes
  const sanitizedPath = path.replace(/\/$/, '').replace(/^\//, '');

  const simplePage = simplePagePaths.get(sanitizedPath);

  if (simplePage) {
    return simplePage;
  }

  for (const [pathRegex, parametrizedPage] of parametrizedPagePaths) {
    if (pathRegex.test(sanitizedPath)) {
      return parametrizedPage;
    }
  }

  return undefined;
}
